/* ============================================================
   DOMAIN MODEL (Frontend / UI)
   ============================================================ */

export interface Restaurant {
  id: string;
  ownerId?: string;
  name: string;

  slug?: string;
  subHeading?: string;
  description?: string;

  logoUrl?: string;
  coverImageUrl?: string;
  themeColor?: string;

  contactPhone?: string;
  contactEmail?: string;
  website?: string;

  street?: string;
  houseNumber?: string;
  postalCode?: string;
  city?: string;
  country?: string;
  /** IANA timezone identifier, e.g. "Europe/Berlin". */
  timezone?: string;

  latitude?: number;
  longitude?: number;

  /** Backend: zones with maxDistanceKm, deliveryFee, minOrderAmount, displayOrder */
  deliveryZones?: { maxDistanceKm: number; deliveryFee: number; minOrderAmount: number; displayOrder?: number }[];
  deliveryMinOrderAmount?: number;
  deliveryFee?: number;
  currency?: string;

  isActive: boolean;
  isStoreOpen?: boolean;
  isDeliveryOpen?: boolean;
  /** Backend: isClosed. When true, store/delivery toggles are disabled. */
  isClosed?: boolean;
  isTemporarilyClosed?: boolean;
  /** Backend: closeMessage. Shown when restaurant is closed. */
  closedReason?: string;
  closeMessage?: string;

  hasPickup?: boolean;
  hasDelivery?: boolean;

  isOnBoardingCompleted?: boolean;
  onBoardingStep?: number;

  openingHours?: DayHours[];
  deliveryHours?: DayHours[];

  /** Estimated delivery duration per day/slot (min–max minutes). */
  deliveryTimeRanges?: TimeRangesByDay[];
  /** Estimated time until ready for pickup (Abholung) per day/slot. */
  pickupTimeRanges?: TimeRangesByDay[];

  socialLinks?: Record<string, string>;
  languagesSupported?: string[];
  paymentMethods?: string[];

  seoMetaTitle?: string;
  seoMetaDescription?: string;
  seoKeywords?: string;

  customDomain?: string;
  subdomain?: string;
  isDomainVerified?: boolean;
  domainVerifiedAt?: string;

  isVerified?: boolean;
  verifiedAt?: string;

  ownerName?: string;
  taxNumber?: string;
  businessNumber?: string;

  /** Legal / policy (optional). URL or inline text/HTML. */
  impressum?: string;
  datenschutz?: string;
  agb?: string;
  widerruf?: string;
  cookie?: string;
  odr?: string;
  alkohol?: string;

  createdAt?: string;
  updatedAt?: string;
}

/* ============================================================
   API RESPONSE (matches Spring Boot RestaurantMapper exactly)
   ============================================================ */

export interface RestaurantResponse {
  id: string;
  ownerId?: string;
  isActive?: boolean;

  // Basic Info
  name?: string;
  subHeading?: string;
  description?: string;
  slug?: string;

  // Media - backend uses logoUrl/coverImageUrl (not logo/coverImage)
  logoUrl?: string;
  coverImageUrl?: string;
  themeColor?: string;
  galleryPhotos?: string[]; // max 10 URLs

  // Contact
  contactEmail?: string;
  contactPhone?: string;

  // Address
  street?: string;
  streetNumber?: string;
  houseNumber?: string;
  postalCode?: string;
  city?: string;
  country?: string;
  /** IANA timezone identifier, e.g. "Europe/Berlin". */
  timezone?: string;
  latitude?: number;
  longitude?: number;

  // Business Details
  ownerName?: string;
  taxNumber?: string;
  vatNumber?: string;
  businessNumber?: string;

  // Delivery Configuration (matches backend DeliveryZoneDTO)
  deliveryZones?: { maxDistanceKm: number; deliveryFee: number; minOrderAmount: number; displayOrder?: number }[];
  deliveryMinOrderAmount?: number;
  deliveryFee?: number;
  currency?: string;

  // Operations Status
  isStoreOpen?: boolean;
  isDeliveryOpen?: boolean;
  /** Backend: isClosed. When true, restaurant is marked closed (e.g. holiday). */
  isClosed?: boolean;
  /** Backend: isTemporarilyClosed. When true, restaurant is temporarily closed. */
  isTemporarilyClosed?: boolean;
  /** Backend field: message when restaurant is closed. Mapped to closedReason for UI. */
  closeMessage?: string;
  /** UI/legacy: use closeMessage from backend. */
  closedReason?: string;

  // Hours (backend sends Map<day, Array<{open, close}>>; frontend also supports DayHours[])
  openingHours?: DayHours[] | Record<string, Array<{ open: string; close: string }>>;
  deliveryHours?: DayHours[] | Record<string, Array<{ open: string; close: string }>>;

  // Estimated delivery/pickup time per day/slot
  deliveryTimeRanges?: TimeRangesByDay[];
  pickupTimeRanges?: TimeRangesByDay[];

  // Domain
  customDomain?: string;
  subdomain?: string;
  isDomainVerified?: boolean;
  domainVerifiedAt?: string;
  domainStatus?: 'NONE' | 'PENDING_VERIFICATION' | 'ACTIVE' | 'FAILED';

  // Subscription (projected from owner account by backend)
  subscriptionPlan?: string;
  subscriptionStatus?: string;
  subscriptionCurrentPeriodEnd?: string;

  // Settings
  socialLinks?: Record<string, string>;
  languagesSupported?: string[];
  /** Default language code (e.g. "eng", "deu"). Returned by API. */
  defaultLanguage?: string;
  paymentMethods?: string[];

  // Stripe Connect — per-mode status lives at account scope (see @/features/payments).
  // This restaurant-level field records which mode the restaurant routes through.
  stripePaymentMode?: 'test' | 'prod';

  // Delivery/Pickup Options
  hasPickup?: boolean;
  hasDelivery?: boolean;

  // Reservations
  maxSeatingCapacity?: number;
  reservationDurationHours?: number;
  reservationAdvanceDays?: number;
  reservationMinAdvanceHours?: number;

  // SEO
  seoMetaTitle?: string;
  seoMetaDescription?: string;
  seoKeywords?: string;

  // Legal / policy (optional String fields; backend: impressum, datenschutz, agb, widerruf, cookie, odr, alkohol)
  impressum?: string;
  datenschutz?: string;
  agb?: string;
  widerruf?: string;
  cookie?: string;
  odr?: string;
  alkohol?: string;
  legalPages?: Record<string, string>; // Legacy map; prefer top-level fields above

  // Verification
  isVerified?: boolean;
  verifiedAt?: string;

  // Timestamps
  createdAt?: string;
  updatedAt?: string;

  // Onboarding
  onBoardingStep?: number;
  isOnBoardingCompleted?: boolean;
}

/* ============================================================
   START ONBOARDING REQUEST (minimal DTO)
   ============================================================ */

/**
 * Minimal DTO for starting restaurant onboarding.
 * Only requires name and slug - all other fields will be set with defaults.
 */
export interface StartOnboardingRequest {
  name: string; // 2–100 chars
  slug: string; // 3–100 chars, lowercase letters, numbers, hyphens only
}

/* ============================================================
   UNIFIED REQUEST DTOs (matching backend)
   ============================================================ */

/**
 * DTO for creating a new restaurant.
 * All fields marked as required in backend must be provided.
 */
export interface RestaurantCreateRequest {
  // Required: Basic Information
  name: string; // 2–100 chars
  slug: string; // 3–100 chars, lowercase letters, numbers, hyphens only
  description: string; // 10–1000 chars

  // Optional: Basic Information
  subHeading?: string; // max 150 chars

  // Media (file uploads or URLs)
  logoFile?: File | null;
  coverImageFile?: File | null;
  logoUrl?: string; // max 500 chars
  coverImageUrl?: string; // max 500 chars
  themeColor?: string; // hex color code

  // Required: Contact
  contactEmail: string; // valid email
  contactPhone: string; // valid phone number

  // Required: Address
  street: string;
  houseNumber?: string; // Optional
  postalCode: string;
  city: string;
  country: string;
  /** Required IANA timezone identifier, e.g. "Europe/Berlin". */
  timezone: string;

  // Required: Business Details
  ownerName: string; // 2–100 chars
  taxNumber: string; // max 50 chars
  businessNumber: string; // max 50 chars

  // Required: Delivery Configuration
  currency: string; // 3-letter code (e.g., USD, EUR)
  hasPickup: boolean;
  hasDelivery: boolean;

  // Optional: Delivery settings (required if hasDelivery is true)
  deliveryMinOrderAmount?: number;
  deliveryFee?: number;

  // Required: Operations
  openingHours: DayHours[];
  deliveryHours?: DayHours[]; // required if hasDelivery is true
  isStoreOpen: boolean;
  isDeliveryOpen: boolean;

  // Optional: Domain Configuration
  customDomain?: string; // max 200 chars
  subdomain?: string; // 3–50 chars, lowercase letters, numbers, hyphens only

  // Optional: Settings
  socialLinks?: Record<string, string>;
  languagesSupported?: string[];
  paymentMethods?: string[];

  // Optional: SEO
  seoMetaTitle?: string; // max 60 chars
  seoMetaDescription?: string; // max 160 chars
  seoKeywords?: string; // max 500 chars

  // Optional: Gallery Photos
  galleryPhotoFiles?: File[]; // For file uploads (multipart/form-data) - max 10 files
  galleryPhotos?: string[]; // For direct URLs (application/json) - max 10 URLs, max 500 chars each

  // Optional: Estimated delivery/pickup time per day/slot
  deliveryTimeRanges?: TimeRangesByDay[];
  pickupTimeRanges?: TimeRangesByDay[];

  // Optional: Legal / policy (URL or inline text/HTML)
  impressum?: string;
  datenschutz?: string;
  agb?: string;
  widerruf?: string;
  cookie?: string;
  odr?: string;
  alkohol?: string;
}

/**
 * Unified DTO for updating restaurant information during onboarding or after.
 * All fields are optional to support partial updates.
 */
export interface RestaurantUpdateRequest {
  // Basic Information
  name?: string; // 2–100 chars
  subHeading?: string; // max 150 chars
  description?: string; // 10–1000 chars (min 10 if provided)
  slug?: string; // 3–100 chars, lowercase letters, numbers, hyphens only

  // Media (file uploads or URLs)
  logoFile?: File | null;
  coverImageFile?: File | null;
  logoUrl?: string; // max 500 chars
  coverImageUrl?: string; // max 500 chars
  themeColor?: string; // hex color code

  // Contact
  contactEmail?: string; // valid email
  contactPhone?: string; // valid phone number

  // Address
  street?: string;
  houseNumber?: string;
  postalCode?: string;
  city?: string;
  country?: string;
  /** IANA timezone identifier, e.g. "Europe/Berlin". */
  timezone?: string;

  // Business Details
  ownerName?: string; // 2–100 chars
  taxNumber?: string; // max 50 chars
  businessNumber?: string; // max 50 chars

  // Delivery Configuration
  deliveryZones?: { maxDistanceKm: number; deliveryFee: number; minOrderAmount: number; displayOrder?: number }[];
  deliveryMinOrderAmount?: number;
  deliveryFee?: number;
  currency?: string; // 3-letter code
  hasPickup?: boolean;
  hasDelivery?: boolean;

  // Operations
  openingHours?: DayHours[];
  deliveryHours?: DayHours[];
  isStoreOpen?: boolean;
  isDeliveryOpen?: boolean;
  /** Mark restaurant as closed (e.g. holiday). When true, store/delivery toggles are disabled. */
  isClosed?: boolean;
  /** Message shown when restaurant is closed (e.g. "Closed for holiday until 15 Feb"). */
  closeMessage?: string;

  // Estimated delivery/pickup time per day/slot (optional)
  deliveryTimeRanges?: TimeRangesByDay[];
  pickupTimeRanges?: TimeRangesByDay[];

  // Domain Configuration
  customDomain?: string; // max 200 chars
  subdomain?: string; // 3–50 chars, lowercase letters, numbers, hyphens only

  // Settings
  socialLinks?: Record<string, string>;
  languagesSupported?: string[];
  defaultLanguage?: string;
  paymentMethods?: string[];

  // SEO
  seoMetaTitle?: string; // max 60 chars
  seoMetaDescription?: string; // max 160 chars
  seoKeywords?: string; // max 500 chars

  // Gallery Photos
  galleryPhotoFiles?: File[]; // For file uploads (multipart/form-data) - max 10 files
  galleryPhotos?: string[]; // For direct URLs (application/json) - max 10 URLs, max 500 chars each

  // Legal / policy (optional; partial update: only set if non-null in request)
  impressum?: string;
  datenschutz?: string;
  agb?: string;
  widerruf?: string;
  cookie?: string;
  odr?: string;
  alkohol?: string;
  legalPages?: Record<string, string>; // Legacy; prefer top-level fields above

  // Onboarding Step Override (optional)
  onboardingStep?: number; // 0–6

  /** Which Stripe Connect mode (test/prod) this restaurant routes through. */
  stripePaymentMode?: 'test' | 'prod';
}

/* ============================================================
   INTERNAL FORM TYPES (used by form components, not API)
   ============================================================ */

/**
 * Internal types used by form components for step-by-step data collection.
 * These are converted to RestaurantCreateRequest/RestaurantUpdateRequest before API calls.
 */
export interface RestaurantBasicInfoRequest {
  name: string;        // 2–100 chars
  subHeading?: string;  // max 150 chars
  description?: string; // 10–1000 chars
  slug: string;        // 3–100 chars, unique, URL safe
}

export interface RestaurantMediaRequest {
  logoFile?: File | null;
  coverImageFile?: File | null;

  logoUrl?: string;
  coverImageUrl?: string;
  themeColor?: string;
}

export interface RestaurantContactRequest {
  contactPhone: string;
  contactEmail: string;
  website?: string;

  street?: string;
  houseNumber?: string;
  postalCode?: string;
  city?: string;
  country?: string;
  /** IANA timezone identifier, e.g. "Europe/Berlin". */
  timezone?: string;
}

export interface RestaurantBusinessRequest {
  ownerName: string;

  taxNumber?: string;
  businessNumber?: string;

  deliveryMinOrderAmount?: number;
  deliveryFee?: number;

  currency: string;

  hasPickup: boolean;
  hasDelivery: boolean;
}

/* ============================================================
   HOURS & OPERATIONS
   ============================================================ */

export interface TimeRange {
  openTime: string;   // "09:00"
  closeTime: string;  // "22:00"
}

/** One time slot with min/max duration in minutes (delivery or pickup estimate). */
export interface TimeRangeWithDuration {
  openTime: string;   // "HH:mm" 24h
  closeTime: string;  // "HH:mm" 24h
  minMinutes: number;
  maxMinutes: number;
}

/** One day: list of slots with duration (or closed). */
export interface TimeRangesByDay {
  day: 'MONDAY' | 'TUESDAY' | 'WEDNESDAY' | 'THURSDAY' | 'FRIDAY' | 'SATURDAY' | 'SUNDAY';
  isClosed: boolean;
  ranges: TimeRangeWithDuration[];
}

export interface DayHours {
  day:
  | 'MONDAY'
  | 'TUESDAY'
  | 'WEDNESDAY'
  | 'THURSDAY'
  | 'FRIDAY'
  | 'SATURDAY'
  | 'SUNDAY';

  isClosed?: boolean;
  ranges: TimeRange[];
}

export interface RestaurantOperationsRequest {
  openingHours?: DayHours[];
  deliveryHours?: DayHours[];

  isStoreOpen?: boolean;
  isDeliveryOpen?: boolean;

  customDomain?: string;
  subdomain?: string;
}

/* ============================================================
   STEP 6 – SEO (legacy, kept for backward compatibility)
   ============================================================ */

export interface RestaurantSeoRequest {
  seoMetaTitle?: string;
  seoMetaDescription?: string;
  seoKeywords?: string;
}

export interface RestaurantSettingsFormRequest {
  metaTitle?: string;
  metaDescription?: string;
  keywords?: string;
  socialLinks?: Record<string, string>;
  languagesSupported?: string[];
  paymentMethods?: string[];
}

/* ============================================================
   SETTINGS REQUEST
   ============================================================ */

/**
 * DTO for updating restaurant settings (social links, languages, payment methods).
 * Can be updated independently of onboarding flow.
 * All fields are optional - only provided fields will be updated.
 */
export interface SettingsRequest {
  /**
   * Social links as a map: { "facebook": "url", "instagram": "url", ... }
   * Optional - can be null or empty map to clear all social links
   */
  socialLinks?: Record<string, string>;

  /**
   * Languages supported: ["en", "de", "it", ...]
   * Optional - if provided, must contain at least one language
   */
  languagesSupported?: string[];

  /**
   * Payment methods: ["CASH", "ONLINE"] - CASH (Bar), ONLINE (Stripe)
   * Optional - if provided, must contain at least one payment method
   */
  paymentMethods?: string[];
}

/**
 * @deprecated Use SettingsRequest instead
 */
export interface RestaurantSettingsRequest extends SettingsRequest { }

/* ============================================================
   MAPPER – API → UI DOMAIN
   ============================================================ */

export function mapRestaurantResponseToRestaurant(
  r: RestaurantResponse
): Restaurant {
  return {
    id: r.id,
    ownerId: r.ownerId,
    name: r.name ?? 'Unnamed Restaurant',

    slug: r.slug,
    subHeading: r.subHeading,
    description: r.description,

    logoUrl: r.logoUrl,
    coverImageUrl: r.coverImageUrl,
    themeColor: r.themeColor ?? '#FF5733',

    contactPhone: r.contactPhone,
    contactEmail: r.contactEmail,

    street: r.street,
    houseNumber: r.houseNumber,
    postalCode: r.postalCode,
    city: r.city,
    country: r.country,
    latitude: r.latitude,
    longitude: r.longitude,

    deliveryZones: r.deliveryZones,
    deliveryMinOrderAmount: r.deliveryMinOrderAmount,
    deliveryFee: r.deliveryFee,
    currency: r.currency,

    isActive: r.isActive ?? false,
    isStoreOpen: r.isStoreOpen,
    isDeliveryOpen: r.isDeliveryOpen,
    isClosed: r.isClosed,
    isTemporarilyClosed: r.isTemporarilyClosed,
    closedReason: r.closeMessage ?? r.closedReason,
    closeMessage: r.closeMessage,

    hasPickup: r.hasPickup,
    hasDelivery: r.hasDelivery,

    isOnBoardingCompleted: r.isOnBoardingCompleted,
    onBoardingStep: r.onBoardingStep,

    openingHours: Array.isArray(r.openingHours) ? r.openingHours : undefined,
    deliveryHours: Array.isArray(r.deliveryHours) ? r.deliveryHours : undefined,
    deliveryTimeRanges: Array.isArray(r.deliveryTimeRanges) ? r.deliveryTimeRanges : undefined,
    pickupTimeRanges: Array.isArray(r.pickupTimeRanges) ? r.pickupTimeRanges : undefined,

    socialLinks: r.socialLinks,
    languagesSupported: r.languagesSupported,
    paymentMethods: r.paymentMethods,

    seoMetaTitle: r.seoMetaTitle,
    seoMetaDescription: r.seoMetaDescription,
    seoKeywords: r.seoKeywords,

    customDomain: r.customDomain,
    subdomain: r.subdomain,
    isDomainVerified: r.isDomainVerified,
    domainVerifiedAt: r.domainVerifiedAt,

    isVerified: r.isVerified,
    verifiedAt: r.verifiedAt,

    ownerName: r.ownerName,

    impressum: r.impressum ?? r.legalPages?.impressum,
    datenschutz: r.datenschutz ?? r.legalPages?.datenschutz,
    agb: r.agb ?? r.legalPages?.agb,
    widerruf: r.widerruf ?? r.legalPages?.widerrufsbelehrung ?? r.legalPages?.widerruf,
    cookie: r.cookie ?? r.legalPages?.cookieBanner ?? r.legalPages?.cookie,
    odr: r.odr ?? r.legalPages?.odrNotice ?? r.legalPages?.odr,
    alkohol: r.alkohol ?? r.legalPages?.alcoholNotice ?? r.legalPages?.alkohol,

    createdAt: r.createdAt,
    updatedAt: r.updatedAt,
  };
}

/* ============================================================
   HELPER – MERGE PATCH DATA (step-wise onboarding)
   ============================================================ */

export function mergeRestaurant(
  base: Restaurant,
  patch: Partial<Restaurant>
): Restaurant {
  return {
    ...base,
    ...patch,
  };
}

/* ============================================================
   CUSTOM DOMAIN API TYPES
   ============================================================ */

export interface DomainSetupInstructions {
  step1: string;
  record: { type: string; name: string; value: string; ttl: string; note?: string };
  step1b?: string;
  ownershipTxtRecord?: { type: string; name: string; value: string; ttl: string };
  step1c?: string;
  txtRecord?: { type: string; name: string; value: string; ttl: string };
  step1d?: string;
  wwwCnameRecord?: { type: string; name: string; value: string; ttl: string };
  step1e?: string;
  wwwOwnershipTxtRecord?: { type: string; name: string; value: string; ttl: string };
  step1f?: string;
  wwwTxtRecord?: { type: string; name: string; value: string; ttl: string };
  step2: string;
}

export interface DomainStatusResponse {
  domain: string | null;
  status: 'NONE' | 'PENDING_VERIFICATION' | 'ACTIVE' | 'FAILED';
  isVerified: boolean;
  verifiedAt: string | null;
  cnameTarget: string;
  setupInstructions: DomainSetupInstructions | null;
  verificationErrors?: string[];
}

export interface DnsRecordCheck {
  type: string;
  name: string;
  value: string;
  status: 'LIVE' | 'WRONG' | 'MISSING' | 'PROPAGATING';
  description: string;
}

export interface DnsCheckResponse {
  domain: string;
  domainStatus: string;
  allLive: boolean;
  usesTxtValidation: boolean;
  autoVerifyTriggered: boolean;
  records: DnsRecordCheck[];
}
